import { useState } from 'react';
import type { CwDataset, CwSequentialScheduleState, CwSyncOutcome } from '@catalyst-psa/shared';
import { CW_DATASET_LABELS, CW_IMPLEMENTED_DATASETS } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { formatDateTime } from '../../utils/date.js';
import { useRunCwSequentialSchedule, useUpdateCwSequentialSchedule } from './api.js';

const OUTCOME_TONES: Record<CwSyncOutcome, 'green' | 'amber' | 'red' | 'blue'> = {
  ok: 'green',
  partial: 'amber',
  failed: 'red',
  running: 'blue',
};

interface SequentialScheduleCardProps {
  schedule: CwSequentialScheduleState;
  isAdmin: boolean;
  configured: boolean;
}

/**
 * A single schedule that syncs a chosen, ordered list of datasets one after another —
 * the next dataset starts only once the previous one finishes.
 */
export function SequentialScheduleCard({
  schedule,
  isAdmin,
  configured,
}: SequentialScheduleCardProps): JSX.Element {
  const update = useUpdateCwSequentialSchedule();
  const run = useRunCwSequentialSchedule();

  // Local edit state seeded once; live status still comes from the polled `schedule` prop.
  const [enabled, setEnabled] = useState(schedule.enabled);
  const [intervalMinutes, setIntervalMinutes] = useState(
    schedule.intervalMinutes ? String(schedule.intervalMinutes) : '60',
  );
  const [order, setOrder] = useState<CwDataset[]>(schedule.datasets);

  const canControl = isAdmin;
  const isRunning = schedule.running;
  const available = CW_IMPLEMENTED_DATASETS.filter((d) => !order.includes(d));

  const addDataset = (dataset: CwDataset): void =>
    setOrder((prev) => (prev.includes(dataset) ? prev : [...prev, dataset]));
  const removeDataset = (dataset: CwDataset): void =>
    setOrder((prev) => prev.filter((d) => d !== dataset));
  const move = (index: number, direction: -1 | 1): void =>
    setOrder((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const a = next[index];
      const b = next[target];
      if (a === undefined || b === undefined) return prev;
      next[index] = b;
      next[target] = a;
      return next;
    });

  const handleSave = (): void => {
    const minutes = Number(intervalMinutes);
    update.mutate({
      enabled,
      intervalMinutes: enabled && minutes > 0 ? Math.floor(minutes) : null,
      datasets: order,
    });
  };

  const handleRun = (): void => run.mutate();

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">Sequential sync schedule</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            One schedule runs the datasets below in order — each starts as soon as the previous
            finishes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isRunning ? (
            <Badge tone="blue">
              {schedule.currentDataset
                ? `Syncing ${CW_DATASET_LABELS[schedule.currentDataset]}…`
                : 'Running…'}
            </Badge>
          ) : schedule.lastOutcome ? (
            <Badge tone={OUTCOME_TONES[schedule.lastOutcome]}>{schedule.lastOutcome}</Badge>
          ) : (
            <Badge tone="gray">Never run</Badge>
          )}
        </div>
      </div>

      <dl className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Last run</dt>
          <dd className="text-slate-800">{formatDateTime(schedule.lastRunAt)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">Next run</dt>
          <dd className="text-slate-800">{formatDateTime(schedule.nextRunAt)}</dd>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <dt className="text-xs uppercase tracking-wide text-slate-500">Last result</dt>
          <dd className="text-slate-800">{schedule.lastMessage ?? '—'}</dd>
        </div>
      </dl>

      {/* Ordered dataset list */}
      <div className="mb-4 rounded-lg bg-slate-50 p-3">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Order of operations
        </span>
        {order.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">
            No datasets added yet. Add datasets below to build the sync order.
          </p>
        ) : (
          <ol className="mt-2 space-y-1">
            {order.map((dataset, index) => (
              <li
                key={dataset}
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm"
              >
                <span className="w-5 shrink-0 tabular-nums text-slate-400">{index + 1}.</span>
                <span className="flex-1 font-medium text-slate-800">
                  {CW_DATASET_LABELS[dataset]}
                  {isRunning && schedule.currentDataset === dataset ? (
                    <span className="ml-2 text-xs font-normal text-brand-600">syncing…</span>
                  ) : null}
                </span>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    aria-label={`Move ${CW_DATASET_LABELS[dataset]} up`}
                    onClick={() => move(index, -1)}
                    disabled={!canControl || index === 0}
                    className="rounded px-1.5 py-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${CW_DATASET_LABELS[dataset]} down`}
                    onClick={() => move(index, 1)}
                    disabled={!canControl || index === order.length - 1}
                    className="rounded px-1.5 py-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => removeDataset(dataset)}
                    disabled={!canControl}
                    className="rounded px-1.5 py-0.5 text-rose-600 hover:bg-rose-50 disabled:opacity-30"
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ol>
        )}

        {available.length > 0 ? (
          <div className="mt-2 flex items-center gap-2">
            <label className="text-sm text-slate-600" htmlFor="cw-seq-add">
              Add dataset
            </label>
            <select
              id="cw-seq-add"
              value=""
              disabled={!canControl}
              onChange={(e) => {
                if (e.target.value) addDataset(e.target.value as CwDataset);
              }}
              className="rounded-lg border border-slate-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
            >
              <option value="">Choose…</option>
              {available.map((dataset) => (
                <option key={dataset} value={dataset}>
                  {CW_DATASET_LABELS[dataset]}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {/* Cadence */}
      <div className="mb-4 rounded-lg bg-slate-50 p-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            disabled={!canControl}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="font-medium text-slate-700">Scheduled sequential sync</span>
        </label>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-sm text-slate-600">Every</span>
          <input
            type="number"
            min={5}
            value={intervalMinutes}
            disabled={!canControl || !enabled}
            onChange={(e) => setIntervalMinutes(e.target.value)}
            className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
          />
          <span className="text-sm text-slate-600">minutes</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleSave}
            isLoading={update.isPending}
            disabled={!canControl}
            className="ml-auto"
          >
            Save schedule
          </Button>
        </div>
      </div>

      {update.isError ? (
        <div className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
          Could not save the schedule. Check the cadence and datasets.
        </div>
      ) : null}
      {run.isError ? (
        <div className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
          Could not start the sequence. Add at least one dataset first.
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={handleRun}
          isLoading={run.isPending || isRunning}
          disabled={!canControl || !configured || isRunning || order.length === 0}
        >
          Run sequence now
        </Button>
        <span className="text-xs text-slate-500">
          Runs {order.length} dataset{order.length === 1 ? '' : 's'} in order, in the background.
        </span>
      </div>
    </div>
  );
}
